import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import { api } from "../lib/api";
import { parseFieldErrors } from "../lib/formErrors";
import DatePickerIT from "./ui/date-picker-it";
import { useModalA11y } from "../lib/useModalA11y";
import { useUnsavedChanges } from "../lib/useUnsavedChanges";

/**
 * Bug 18/50 · Modal per creare un'AZIONE dentro un CICLO.
 * Diverso da `CreateAzioneModal` (che è per le mete/personal_goals).
 * Qui creiamo un Task con `parent_task_id = cycle.id`, `type='task'` forzato server-side.
 *
 * Focus retention (Bug 1): usa `useModalA11y` con dependency stabili → nessun
 * focus-steal quando l'utente digita nel textarea (>50 char OK).
 */
export default function AzioneCicloForm({ open, onClose, onSaved, cycle }) {
  const [form, setForm] = useState({
    title: "", description: "",
    scheduled_date: "", scheduled_time: "",
  });
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dialogRef = useRef(null);

  useModalA11y(dialogRef, open, onClose);
  useUnsavedChanges(dirty && open);

  useEffect(() => {
    if (!open) return;
    setDirty(false);
    setForm({ title: "", description: "", scheduled_date: "", scheduled_time: "" });
  }, [open]);

  const change = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const submit = async () => {
    if (form.title.trim().length < 2) {
      toast.error("Il titolo dell'azione deve avere almeno 2 caratteri");
      return;
    }
    if (!cycle?.id) { toast.error("Ciclo mancante"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        scheduled_date: form.scheduled_date || null,
        scheduled_time: form.scheduled_time || null,
        all_day: !form.scheduled_time,
      };
      await api.createCycleAction(cycle.id, payload);
      toast.success("Azione creata");
      setDirty(false);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(parseFieldErrors(e, "Errore creazione azione"));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="azione-ciclo-modal">
      <div
        ref={dialogRef}
        role="dialog" aria-modal="true" aria-labelledby="azione-ciclo-title"
        className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 id="azione-ciclo-title" className="font-display text-xl font-semibold">
            Nuova azione nel ciclo
          </h3>
          <button onClick={onClose} data-testid="azione-ciclo-close" aria-label="Chiudi">
            <X size={20}/>
          </button>
        </div>
        {cycle?.title && (
          <div className="text-xs opacity-70 bg-black/5 rounded-lg px-2.5 py-1.5"
               data-testid="azione-ciclo-parent-hint">
            Ciclo: <strong>{cycle.title}</strong>
          </div>
        )}
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Titolo *</div>
          <input
            className="pxos-input" maxLength={500}
            value={form.title}
            onChange={(e) => change({ title: e.target.value })}
            data-testid="azione-ciclo-title-input"
          />
        </label>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Descrizione</div>
          <textarea
            className="pxos-input" rows={3}
            value={form.description}
            onChange={(e) => change({ description: e.target.value })}
            data-testid="azione-ciclo-description-input"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Data programmata (opzionale)</div>
            <DatePickerIT
              value={form.scheduled_date}
              onChange={(v) => change({ scheduled_date: v })}
              testId="azione-ciclo-scheduled-date"
              placeholder="gg/mm/aaaa"
            />
          </label>
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Ora (opzionale)</div>
            <input
              type="time" className="pxos-input"
              value={form.scheduled_time}
              onChange={(e) => change({ scheduled_time: e.target.value })}
              disabled={!form.scheduled_date}
              data-testid="azione-ciclo-scheduled-time"
            />
          </label>
        </div>
        <div className="text-xs opacity-60">
          Senza data l'azione resta nel ciclo (non appare in Agenda).
          Con data compare in Agenda solo il giorno indicato.
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button
            onClick={onClose} className="text-sm underline opacity-70"
            data-testid="azione-ciclo-cancel"
          >
            Annulla
          </button>
          <button
            disabled={busy} onClick={submit}
            className="pxos-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="azione-ciclo-submit"
          >
            <Check size={14}/> Crea azione
          </button>
        </div>
      </div>
    </div>
  );
}
